import { useEffect, useMemo, useState, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { AtSign } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useGuildMembers, useGuildRoles } from '@/lib/mentions';

type Field = HTMLTextAreaElement | HTMLInputElement;

interface Option {
  key: string;
  label: string;
  hint: string;
  token: string;
  color?: string;
  avatarUrl?: string | null;
}

/** `@abc` right before the cursor (after a space or at the start) → "abc". */
function mentionQuery(el: Field): { query: string; start: number } | null {
  const caret = el.selectionStart ?? el.value.length;
  if (caret !== (el.selectionEnd ?? caret)) return null;
  const m = el.value.slice(0, caret).match(/(?:^|\s)@([^\s@<>]{0,32})$/);
  return m ? { query: m[1], start: caret - m[1].length - 1 } : null;
}

/** Replaces [start, end) through the native setter + `input`, so controlled React fields follow. */
function replaceRange(el: Field, start: number, end: number, text: string) {
  const next = el.value.slice(0, start) + text + el.value.slice(end);
  if (el.maxLength > 0 && next.length > el.maxLength) return;
  const proto = el instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
  Object.getOwnPropertyDescriptor(proto, 'value')?.set?.call(el, next);
  el.dispatchEvent(new Event('input', { bubbles: true }));
  el.focus();
  const caret = start + text.length;
  el.setSelectionRange(caret, caret);
}

/** Names starting with the typed letters come first. */
const rank = (q: string, name: string) => (name.toLowerCase().startsWith(q) ? 0 : 1);

/**
 * Discord-style mentions for message text fields: typing `@` + a few letters
 * lists matching roles (in their colour), members and @everyone / @here under
 * the field; ↑↓ + Enter / Tab or a click inserts `<@&id>` / `<@id>`. Roles and
 * members load on the first `@`.
 */
export function MentionSuggest({ target, guildId }: { target: RefObject<Field | null>; guildId: string }) {
  const [match, setMatch] = useState<{ query: string; start: number } | null>(null);
  const [active, setActive] = useState(0);
  const [used, setUsed] = useState(false);
  const roles = useGuildRoles(guildId, used);
  const members = useGuildMembers(guildId, used);

  const options = useMemo<Option[]>(() => {
    if (!match) return [];
    const q = match.query.toLowerCase();
    const special = ['everyone', 'here']
      .filter((s) => s.startsWith(q))
      .map((s) => ({ key: s, label: `@${s}`, hint: 'alle', token: `@${s}` }));
    const roleOpts = (roles.data?.roles ?? [])
      .filter((r) => r.name !== '@everyone' && r.name.toLowerCase().includes(q))
      .sort((a, b) => rank(q, a.name) - rank(q, b.name) || b.position - a.position)
      .slice(0, 5)
      .map((r) => ({
        key: `r${r.id}`,
        label: `@${r.name}`,
        hint: 'Rolle',
        token: `<@&${r.id}>`,
        color: r.color ? `#${r.color.toString(16).padStart(6, '0')}` : undefined,
      }));
    const memberOpts = (members.data?.members ?? [])
      .filter((m) => !m.bot && [m.displayName, m.username, m.rawUsername].some((n) => n?.toLowerCase().includes(q)))
      .sort((a, b) => rank(q, a.displayName || a.username) - rank(q, b.displayName || b.username))
      .slice(0, 6)
      .map((m) => ({ key: `u${m.id}`, label: m.displayName || m.username, hint: 'Person', token: `<@${m.id}>`, avatarUrl: m.avatarUrl }));
    return [...special, ...roleOpts, ...memberOpts].slice(0, 8);
  }, [match, roles.data, members.data]);

  useEffect(() => setActive(0), [match?.query]);

  // Under the field, following scrolling and resizing while open.
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const open = !!match;
  useEffect(() => {
    const el = target.current;
    if (!open || !el) return setPos(null);
    const place = () => {
      const r = el.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: r.left, width: r.width });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, target]);

  // Follow the field: typing, clicking, cursor keys.
  useEffect(() => {
    const el = target.current;
    if (!el) return;
    const check = () => {
      const m = mentionQuery(el);
      setMatch(m);
      if (m) setUsed(true);
    };
    const close = () => setTimeout(() => setMatch(null), 150);
    el.addEventListener('input', check);
    el.addEventListener('click', check);
    el.addEventListener('keyup', check);
    el.addEventListener('blur', close);
    return () => {
      el.removeEventListener('input', check);
      el.removeEventListener('click', check);
      el.removeEventListener('keyup', check);
      el.removeEventListener('blur', close);
    };
  }, [target]);

  const pick = (opt: Option) => {
    const el = target.current;
    if (!el || !match) return;
    replaceRange(el, match.start, el.selectionStart ?? el.value.length, `${opt.token} `);
    setMatch(null);
  };

  // Keys while the list is open — native listener so it runs before the
  // page's own onKeyDown (e.g. Enter = send).
  useEffect(() => {
    const el = target.current;
    if (!el || !match) return;
    const onKey = (e: Event) => {
      const k = (e as KeyboardEvent).key;
      if (k === 'Escape') {
        setMatch(null);
      } else if (options.length && (k === 'ArrowDown' || k === 'ArrowUp')) {
        setActive((a) => (a + (k === 'ArrowDown' ? 1 : options.length - 1)) % options.length);
      } else if (options.length && (k === 'Enter' || k === 'Tab')) {
        pick(options[active] ?? options[0]);
      } else {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
    };
    el.addEventListener('keydown', onKey, true);
    return () => el.removeEventListener('keydown', onKey, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, match, options, active]);

  if (!match || !pos) return null;
  const loading = roles.isLoading || members.isLoading;
  // Portal + fixed position: the glass cards are stacking contexts, an
  // absolute list would slide under the next card.
  return createPortal(
    <div
      role="listbox"
      className="glass-float fixed z-50 overflow-hidden rounded-lg border bg-card text-sm shadow-xl"
      style={{ top: pos.top, left: pos.left, width: pos.width }}
      onMouseDown={(e) => e.preventDefault()}
    >
      {options.length === 0 && (
        <p className="px-3 py-2 text-muted-foreground">{loading ? 'Lade Rollen und Personen…' : 'Keine Rolle oder Person gefunden.'}</p>
      )}
      {options.map((o, i) => (
        <button
          key={o.key}
          type="button"
          role="option"
          aria-selected={i === active}
          onMouseEnter={() => setActive(i)}
          onClick={() => pick(o)}
          className={cn('flex w-full items-center gap-2 px-3 py-1.5 text-left', i === active ? 'bg-accent' : 'hover:bg-accent/60')}
        >
          {o.avatarUrl ? (
            <img src={o.avatarUrl} alt="" className="h-5 w-5 shrink-0 rounded-full" />
          ) : (
            <span
              className="h-2.5 w-2.5 shrink-0 rounded-full"
              style={{ backgroundColor: o.color ?? 'hsl(var(--muted-foreground))' }}
            />
          )}
          <span className="min-w-0 flex-1 truncate font-medium" style={o.color ? { color: o.color } : undefined}>
            {o.label}
          </span>
          <span className="shrink-0 text-xs text-muted-foreground">{o.hint}</span>
        </button>
      ))}
    </div>,
    document.body
  );
}

/** "@" button next to the emoji button: types an @ at the cursor, which opens the list. */
export function MentionButton({
  target,
  className,
  disabled,
}: {
  target: RefObject<Field | null>;
  className?: string;
  disabled?: boolean;
}) {
  return (
    <div className={className}>
      <button
        type="button"
        disabled={disabled}
        title="Rolle oder Person erwähnen"
        aria-label="Rolle oder Person erwähnen"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => {
          const el = target.current;
          if (!el) return;
          const at = el.selectionStart ?? el.value.length;
          const needsSpace = at > 0 && !/\s/.test(el.value[at - 1]);
          replaceRange(el, at, el.selectionEnd ?? at, needsSpace ? ' @' : '@');
        }}
        className={cn(
          'flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors',
          'hover:bg-accent hover:text-foreground disabled:pointer-events-none disabled:opacity-50'
        )}
      >
        <AtSign className="h-4 w-4" />
      </button>
    </div>
  );
}
